import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { format as formatDate } from 'date-fns';

import { Button, Skeleton, Typography } from 'wtb-components';
import { getCurrencyFormat, getDateLocale, isOrdinalLanguage } from 'wtb-utils';
import { Divider, PurchaseBlock } from 'components';
import { useAppContext, useLocaleContext, useTransactionContext } from 'context';
import { APP_STEPS } from 'constants.js';
import clsx from 'clsx';
import styles from './PurchaseInfo.module.css';

const Title = ({ children, className }) => {
  return <Typography className={clsx(styles.title, className)} variant="subtitle1">{children}</Typography>;
};

const PurchaseSkeleton = () => (
  <div>
    <Skeleton width="50%" />
    <Skeleton variant="rect" height={200} />
    <Skeleton variant="rect" height={50} />
  </div>
);

const TableCellContent = ({ children, width, ...other }) => {
  const { isLoading } = useAppContext();

  return (
    <Typography className={clsx({ [styles.skeletonRight]: other.align === 'right' })} {...other}>
      {isLoading
        ? <Skeleton className={styles.skeleton} width={width} aria-label="skeleton" />
        : children}
    </Typography>
  );
};

const PurchaseInfo = ({ detailed }) => {
  const { t: translate } = useTranslation();

  const { isSuccess, setAppStep } = useAppContext();
  const { baseLocale } = useLocaleContext();
  const { order } = useTransactionContext();

  const [ rows, setRows ] = useState([]);
  const [ resultRows, setResultRows ] = useState([]);
  const [ totalRow, setTotalRow ] = useState([]);
  const [ subscriptionRows, setSubscriptionRows ] = useState([]);
  const [ subscriptionResultRows, setSubscriptionResultRows ] = useState([]);
  const [ subscriptionTotalRow, setSubscriptionTotalRow ] = useState([]);
  const [ dateLocale, setDateLocale ] = useState();

  /* const header = [
    translate('purchaseInfo.table.product'),
    translate('purchaseInfo.table.amount'),
    {
      text: translate('purchaseInfo.table.total'),
      className: styles.rightText
    }
  ]; */

  useEffect(() => {
    if (!baseLocale) return;

    const localeDefinition = require('date-fns/locale')[getDateLocale(baseLocale)];
    setDateLocale(localeDefinition);
  }, [ baseLocale ]);

  const getPeriod = useCallback(() => {
    switch (order.periodicity) {
      case 'WEEKLY': return translate('subscription.periods.week');
      case 'BI_WEEKLY': return translate('subscription.periods.biWeek');
      case 'BI_MONTHLY': return translate('subscription.periods.biMonth');
      case 'QUARTERLY': return translate('subscription.periods.quarter');
      case 'HALF_YEARLY': return translate('subscription.periods.halfYear');
      case 'YEARLY': return translate('subscription.periods.year');
      case 'MONTHLY':
      default: return translate('subscription.periods.month');
    }
  }, [ order.periodicity, translate ]);

  // TODO: Prepare for different periods
  const getTotalAmount = useCallback((index) => (
    getCurrencyFormat(order.summaries[index]?.grossValue, order.currency, baseLocale)
  ), [ order.summaries, order.currency, baseLocale ]);

  const getData = useCallback((item, recurrence) => [
    {
      width: '60%',
      className: styles.tableProduct,
      title: item.description,
      content: (
        <TableCellContent className={styles.textPrimary} component="div" variant="body2">
          {item.description}
        </TableCellContent>
      )
    },
    {
      width: '10%',
      content: (
        <TableCellContent component="div" variant="body2" align="right">{item.numberOfItems} x</TableCellContent>
      )
    },
    {
      width: '30%',
      className: clsx({ [styles.tableProductNoSpace]: recurrence }, styles.tableProduct, styles.rightText),
      content: (
        <TableCellContent
          className={clsx({ [styles.spaceCell]: recurrence })}
          variant="body2"
          component="div"
          align="right"
          data-periodicity={order.periodicity}
        >
          <Typography variant="body2" component="span" align="right">
            {getCurrencyFormat(item.grossValue, order.currency, baseLocale)}
          </Typography>
          <Typography variant="caption" align="right">{recurrence}</Typography>
        </TableCellContent>
      )
    }
  ], [ order.currency, order.periodicity, baseLocale ]);

  const getResultRows = useCallback((title, description, isPrimary) => [
    {
      className: styles.tableResultsTitleCell,
      content: (
        <TableCellContent
          component="div"
          variant="subtitle1"
          width="40%"
          color={isPrimary ? 'textPrimary' : 'textSecondary'}
        >
          {title}
        </TableCellContent>
      )
    },
    {
      className: styles.tableResultsCell,
      colSpan: 2,
      content: (
        <TableCellContent
          component="div"
          variant="subtitle1"
          align="right"
          width="60%"
          color={isPrimary ? 'textPrimary' : 'textSecondary'}
        >
          {description}
        </TableCellContent>
      )
    }
  ], []);

  const getSubResultRows = useCallback((title, description) => [
    {
      className: styles.subscriptionInfoTitleCell,
      content: (
        <TableCellContent color="textSecondary" component="div" variant="caption" width="85%">
          {title}
        </TableCellContent>
      )
    },
    {
      className: styles.subscriptionInfoCell,
      colSpan: 2,
      content: (
        <TableCellContent color="textSecondary" component="div" variant="caption" align="right" width="60%">
          {description}
        </TableCellContent>
      )
    }
  ], []);

  const getChargedDate = useCallback((skipCheck) => {
    const date = isOrdinalLanguage(baseLocale)
      ? formatDate(new Date(order.chargeDate), 'do MMMM', { locale: dateLocale })
      : formatDate(new Date(order.chargeDate), 'dd MMMM', { locale: dateLocale });

    if (skipCheck) {
      return date;
    }

    const dateNow = formatDate(new Date(), 'yyyy-MM-dd');
    if (dateNow !== order.chargeDate) {
      return date;
    }

    return `${translate('purchaseInfo.table.today')}, ${date}`;
  }, [ order.chargeDate, baseLocale, dateLocale, translate ]);

  /*
    Weekly on {{weekDayName}}
    Every 2 weeks on {{weekDayName}}
    Monthly on day {{monthDayNumber}}
    Every 2 months on day {{monthDayNumber}}
    Every 3 months on day {{monthDayNumber}}
    Every 6 months on day {{monthDayNumber}}
    Yearly on {{monthName}} {{monthDayOrdinal}}
  */
  const getRecurrenceDate = useCallback(() => {
    const monthDayNumber = formatDate(new Date(order.chargeDate), 'd', { locale: dateLocale });
    const monthDayOrdinal = formatDate(new Date(order.chargeDate), 'do', { locale: dateLocale });
    const monthShortName = formatDate(new Date(order.chargeDate), 'LLL', { locale: dateLocale });
    const monthName = formatDate(new Date(order.chargeDate), 'LLLL', { locale: dateLocale });
    const weekDayName = formatDate(new Date(order.chargeDate), 'EEEE', { locale: dateLocale });

    const options = {
      monthDayNumber,
      monthDayOrdinal,
      monthShortName,
      monthName,
      weekDayName
    };

    switch (order.periodicity) {
      case 'WEEKLY': return translate('subscription.recurrence.week', options);
      case 'BI_WEEKLY': return translate('subscription.recurrence.biWeek', options);
      case 'BI_MONTHLY': return translate('subscription.recurrence.biMonth', options);
      case 'QUARTERLY': return translate('subscription.recurrence.quarter', options);
      case 'HALF_YEARLY': return translate('subscription.recurrence.halfYear', options);
      case 'YEARLY': return translate('subscription.recurrence.year', options);
      case 'MONTHLY':
      default: return translate('subscription.recurrence.month', options);
    }
  }, [ order.chargeDate, order.periodicity, dateLocale, translate ]);

  useEffect(() => {
    if (!order.items) return;

    const _rows = [];
    const _resultRows = [];
    const _subscriptionRows = [];
    const _subscriptionResultRows = [];

    order.items.forEach((item) => {
      if (item.recurring === 'FIRST') {
        _rows.push(getData(item));
      } else if (item.recurring === 'NEXT') {
        _subscriptionRows.push(getData(item, getPeriod()));
      } else if (item.recurring === 'ALL') {
        _rows.push(getData(item));
        _subscriptionRows.push(getData(item, getPeriod()));
      }
    });

    if (detailed) {
      _resultRows.push([
        {
          className: styles.tableResultsCell,
          content: (
            <Typography className={styles.subtotal} variant="body2" component="div">
              {translate('purchaseInfo.table.subTotal')}
            </Typography>
          )
        },
        {
          className: styles.tableResultsCell,
          colSpan: 2,
          content: (
            <Typography className={styles.subtotal} variant="body2" component="div" align="right">
              {getCurrencyFormat(order.summaries[0].netValue, order.currency, baseLocale)}
            </Typography>
          )
        }
      ]);

      order.summaries[0].vat.forEach((vat) => {
        _resultRows.push([
          {
            className: styles.tableResultsCell,
            content: (
              <Typography className={styles.vat} variant="body2" component="div">
                {translate('purchaseInfo.table.vatAmount', { amount: vat.percentage })}
              </Typography>
            )
          },
          {
            className: styles.tableResultsCell,
            colSpan: 2,
            content: (
              <Typography className={styles.vat} variant="body2" component="div" align="right">
                {getCurrencyFormat(vat.value, order.currency, baseLocale)}
              </Typography>
            )
          }
        ]);
      });
    }

    const totalText = (
      <Typography variant="subtitle1">
        <Trans
          i18nKey="purchaseInfo.table.fullTotal"
          components={[ <Typography color="textSecondary" component="span" variant="body2" /> ]}
        />
      </Typography>
    );

    const _totalRows = [ getResultRows(totalText, getTotalAmount(0), true) ];

    if (order.type === 'SUBSCRIPTION') {
      const chargeDate = getChargedDate(isSuccess());

      // 1st table - Payment date
      const subscriptionPaymentDateRows = getSubResultRows(translate('purchaseInfo.table.paymentDate'), chargeDate);
      // const subscriptionPaymentDateRows = getSubResultRows(translate('purchaseInfo.table.chargeDate'), chargeDate);

      // 2nd table - Recurrence
      const subscriptionRecurrenceRows = getSubResultRows(translate('purchaseInfo.table.paymentDate'), getRecurrenceDate());
      // const subscriptionRecurrenceRows = getSubResultRows(translate('purchaseInfo.table.recurrency'), getPeriod());

      const totalSubscriptionRows = getResultRows(totalText, getTotalAmount(1), true);

      _totalRows.push(subscriptionPaymentDateRows);

      setSubscriptionRows([ ..._subscriptionRows ]);
      setSubscriptionResultRows([ ..._subscriptionResultRows ]);
      setSubscriptionTotalRow([ totalSubscriptionRows, subscriptionRecurrenceRows ]);
    }

    setRows([ ..._rows ]);
    setResultRows([ ..._resultRows ]);
    setTotalRow([ ..._totalRows ]);
  }, [
    detailed,
    order.items,
    order.currency,
    order.vat,
    order.summaries,
    order.type,
    isSuccess,
    baseLocale,
    translate,
    getTotalAmount,
    getData,
    getResultRows,
    getSubResultRows,
    getChargedDate,
    getRecurrenceDate,
    getPeriod
  ]);

  const handleGoToClick = () => setAppStep(APP_STEPS.PERSONAL_INFORMATION);

  return order.items
    ? (
      <>
        <PurchaseBlock
          id={`block-${translate('purchaseInfo.title')}`}
          className={styles.paddedBlock}
          title={<Title className={styles.purchaseInfoTitle}>{translate('purchaseInfo.title')}</Title>}
          collapsable={false}
          rows={rows}
          itemsLabel="purchase-table"
          resultRows={resultRows}
          resultLabel="purchase-table-results"
          totalRows={totalRow}
          totalLabel="purchase-table-total"
        />

        {order.type === 'SUBSCRIPTION' && subscriptionRows.length ? (
          <>
            <Divider className={styles.divider} size="mid" />

            <PurchaseBlock
              id="block-subscription"
              className={clsx(styles.paddedBlock, styles.subscriptionBlock)}
              title={<Title className={styles.subscriptionConfirmationTitle}>{translate('purchaseInfo.subTitle')}</Title>}
              collapsable={!detailed}
              defaultExpanded={false}
              rows={subscriptionRows}
              itemsLabel="subscription-table"
              resultRows={subscriptionResultRows}
              resultLabel="subscription-table-result"
              totalRows={subscriptionTotalRow}
              totalLabel="subscription-table-total"
            />
          </>
        ) : ''}

        {!isSuccess() && (
          <div className={styles.buttonBox}>
            <Button
              id="go-personal-info"
              className={styles.button}
              onClick={handleGoToClick}
            >
              {translate('purchaseInfo.goToPersonal')}
            </Button>
          </div>
        )}
      </>
    ) : (
      <PurchaseSkeleton />
    );
};

export default PurchaseInfo;
